/**
 * AdminKnowledge.js
 * Global Knowledge Repository for Admin Portal
 * Manages market intelligence, industry trends, and documents that apply to ALL companies
 * Company-specific documents (Internal Strategy) live in CompanyDetail instead
 * Uses existing /api/knowledge/* backend endpoints
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../AuthContext';
import { Modal, DataTable } from '../components/SharedComponents';
import { API_BASE } from '../config';
import colors from '../utils/adminColors';

// Global-only categories (no internal_strategy — that's company-specific)
// Icons are JSX SVG elements — do NOT use emoji anywhere
const CategoryIcons = {
  market_intelligence: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>,
  news_events: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"><path d="M4 22h16a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v16a2 2 0 0 1-2 2zm0 0a2 2 0 0 1-2-2v-9c0-1.1.9-2 2-2h2"/><line x1="10" y1="6" x2="18" y2="6"/><line x1="10" y1="10" x2="18" y2="10"/><line x1="10" y1="14" x2="14" y2="14"/></svg>,
  best_practices: <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" stroke="none"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>,
  supplier_intelligence: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>,
  regulatory: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>,
  _default: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>,
};

const GLOBAL_CATEGORIES = {
  market_intelligence: { icon: CategoryIcons.market_intelligence, label: 'Market Intelligence', color: colors.accent },
  news_events: { icon: CategoryIcons.news_events, label: 'News & Events', color: colors.success },
  best_practices: { icon: CategoryIcons.best_practices, label: 'Best Practices', color: colors.adminPurple },
  supplier_intelligence: { icon: CategoryIcons.supplier_intelligence, label: 'Supplier Intelligence', color: colors.cyan },
  regulatory: { icon: CategoryIcons.regulatory, label: 'Regulatory', color: colors.danger },
};

const PAGE_SIZE = 25;

function AdminKnowledge() {
  const { token } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedCompanyFilter, setSelectedCompanyFilter] = useState('all'); // 'all', 'global', or a company_id
  const [searchQuery, setSearchQuery] = useState('');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [message, setMessage] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const [uploadForm, setUploadForm] = useState({
    file: null,
    title: '',
    category: 'market_intelligence',
    source: '',
    document_date: '',
    summary: '',
    relevance_tags: '',
    company_id: '',
  });

  useEffect(() => {
    fetchCategories();
    fetchDocuments();
    fetchCompanies();
  }, [selectedCategory, searchQuery]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategory, searchQuery, selectedCompanyFilter]);

  // Auto-dismiss messages
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/knowledge/categories`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      if (response.ok) {
        const allCats = await response.json();
        // Only show global categories (filter out internal_strategy)
        setCategories(allCats.filter(c => c.category_code !== 'internal_strategy'));
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/companies-list`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setCompanies(data.companies || []);
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  };

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      let url = `${API_BASE}/api/knowledge/documents?limit=500`;
      if (selectedCategory) url += `&category=${selectedCategory}`;
      if (searchQuery) url += `&search=${encodeURIComponent(searchQuery)}`;
      const response = await fetch(url, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      if (response.ok) {
        const allDocs = await response.json();
        // Filter out internal_strategy documents from the global view
        setDocuments(allDocs.filter(d => d.category !== 'internal_strategy'));
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadForm.file || !uploadForm.title || !uploadForm.category) {
      setMessage({ type: 'error', text: 'Please fill in required fields (file, title, category)' });
      return;
    }

    setUploading(true);
    const formData = new FormData();
    Object.entries(uploadForm).forEach(([key, value]) => {
      if (key === 'company_id') {
        // Always send company_id: empty string means global (null)
        formData.append(key, value || '');
      } else if (value) {
        formData.append(key, value);
      }
    });

    try {
      const response = await fetch(`${API_BASE}/api/knowledge/upload`, {
        method: 'POST',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        body: formData,
      });

      if (response.ok) {
        const result = await response.json();
        setMessage({ type: 'success', text: `"${result.title}" uploaded successfully!` });
        setShowUploadModal(false);
        resetForm();
        fetchDocuments();
        fetchCategories();
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.detail || 'Upload failed' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Upload failed: ' + error.message });
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (documentId, title) => {
    if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return;

    try {
      const response = await fetch(`${API_BASE}/api/knowledge/documents/${documentId}`, {
        method: 'DELETE',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      });
      if (response.ok) {
        setMessage({ type: 'success', text: `Deleted "${title}"` });
        fetchDocuments();
        fetchCategories();
        if (selectedDocument?.document_id === documentId) setSelectedDocument(null);
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Delete failed' });
    }
  };

  const resetForm = () => {
    setUploadForm({
      file: null,
      title: '',
      category: 'market_intelligence',
      source: '',
      document_date: '',
      summary: '',
      relevance_tags: '',
      company_id: '',
    });
  };

  const formatDate = (d) => {
    if (!d) return 'Not available';
    return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const getCategoryConfig = (cat) => GLOBAL_CATEGORIES[cat] || { icon: CategoryIcons._default, label: 'Other', color: colors.textMuted };

  const totalDocuments = categories.reduce((sum, cat) => sum + (cat.document_count || 0), 0);

  // Apply company filter client-side
  const filteredDocs = documents.filter(doc => {
    if (selectedCompanyFilter === 'all') return true;
    if (selectedCompanyFilter === 'global') return !doc.company_id;
    return doc.company_id === parseInt(selectedCompanyFilter);
  });

  // Pagination
  const totalPages = Math.max(1, Math.ceil(filteredDocs.length / PAGE_SIZE));
  const paginatedDocs = filteredDocs.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  // ── Loading State ──
  if (loading && documents.length === 0) {
    return (
      <div style={{ padding: '32px', display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', backgroundColor: colors.lightGray }}>
        <div style={{ textAlign: 'center' }}>
          
          <div style={{ color: colors.textMuted, fontSize: 'var(--font-size-base)' }}>Loading knowledge repository...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '32px', backgroundColor: colors.lightGray, minHeight: '100vh' }}>
      {/* ── Header Row ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h1 style={{ fontSize: 'var(--font-size-hero)', fontWeight: 'var(--font-weight-bold)', color: colors.darkText, margin: '0 0 4px 0' }}>
            Global Knowledge
          </h1>
          <p style={{ color: colors.textMuted, fontSize: 'var(--font-size-sm)', margin: 0 }}>
            Market intelligence and documents shared across all companies
          </p>
        </div>
        <button
          onClick={() => setShowUploadModal(true)}
          style={{
            display: 'flex', alignItems: 'center', gap: '8px',
            padding: '8px 16px', backgroundColor: colors.adminPurple, color: colors.white,
            border: 'none', borderRadius: '6px', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', cursor: 'pointer',
          }}
        >
          Upload Document
        </button>
      </div>

      {/* ── Message Toast ── */}
      {message && (
        <div style={{
          marginBottom: '16px', padding: '12px 16px', borderRadius: '6px',
          backgroundColor: message.type === 'success' ? `${colors.success}15` : `${colors.danger}15`,
          borderLeft: `3px solid ${message.type === 'success' ? colors.success : colors.danger}`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 'var(--font-size-sm)',
        }}>
          <span style={{ color: message.type === 'success' ? colors.success : colors.danger }}>
            {message.type === 'success' ? '' : ''}{message.text}
          </span>
          <button onClick={() => setMessage(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 'var(--font-size-md)', color: colors.textMuted }}>×</button>
        </div>
      )}

      {/* ── Category Filter Chips + Search ── */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px', flexWrap: 'wrap',
      }}>
        {/* All chip */}
        <button
          onClick={() => setSelectedCategory('')}
          style={{
            padding: '5px 12px', borderRadius: '14px', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)',
            border: !selectedCategory ? `2px solid ${colors.adminPurple}` : `1px solid ${colors.mediumGray}`,
            backgroundColor: !selectedCategory ? colors.adminPurple : colors.white,
            color: !selectedCategory ? colors.white : colors.darkText,
            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px',
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg> All ({totalDocuments})
        </button>

        {categories.map((cat) => {
          const config = getCategoryConfig(cat.category_code);
          const isSelected = selectedCategory === cat.category_code;
          return (
            <button
              key={cat.category_code}
              onClick={() => setSelectedCategory(isSelected ? '' : cat.category_code)}
              style={{
                padding: '5px 12px', borderRadius: '14px', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)',
                border: isSelected ? `2px solid ${config.color}` : `1px solid ${colors.mediumGray}`,
                backgroundColor: isSelected ? config.color : colors.white,
                color: isSelected ? colors.white : colors.darkText,
                cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px',
              }}
            >
              {config.icon} {config.label} ({cat.document_count})
            </button>
          );
        })}

        {/* Company scope filter */}
        <select
          value={selectedCompanyFilter}
          onChange={(e) => setSelectedCompanyFilter(e.target.value)}
          style={{
            marginLeft: 'auto', padding: '5px 10px', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-medium)',
            border: `1px solid ${colors.mediumGray}`, borderRadius: '6px',
            backgroundColor: colors.white, color: colors.darkText, cursor: 'pointer', outline: 'none',
          }}
        >
          <option value="all">All Scopes</option>
          <option value="global">Global Only</option>
          {companies.map(c => (
            <option key={c.company_id} value={c.company_id}>{c.company_name}</option>
          ))}
        </select>

        {/* Search input */}
        <div style={{ position: 'relative', minWidth: '220px' }}>
          <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', fontSize: 'var(--font-size-sm)', color: colors.textMuted }}></span>
          <input
            type="text"
            placeholder="Search documents..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              width: '100%', padding: '6px 10px 6px 30px', fontSize: 'var(--font-size-sm)',
              border: `1px solid ${colors.mediumGray}`, borderRadius: '6px',
              backgroundColor: colors.white, outline: 'none', boxSizing: 'border-box',
            }}
          />
        </div>
      </div>

      {/* ── Main Content: Document Table + Detail Panel ── */}
      <div style={{ display: 'grid', gridTemplateColumns: selectedDocument ? '1fr 320px' : '1fr', gap: '16px' }}>

        {/* Document Table */}
        <div style={{
          backgroundColor: colors.white, borderRadius: '8px',
          boxShadow: 'var(--shadow-sm)', overflow: 'hidden',
        }}>
          <DataTable
            columns={[
              { key: 'title', header: 'Title', render: (val) => <span style={{ fontWeight: 'var(--font-weight-medium)', color: colors.darkText }}>{val}</span> },
              { key: 'category', header: 'Category', width: '130px', render: (val, row) => {
                const config = getCategoryConfig(row.category);
                return <span style={{ padding: '2px 7px', borderRadius: '4px', fontSize: 'var(--font-size-xs)', fontWeight: 'var(--font-weight-semibold)', backgroundColor: `${config.color}12`, color: config.color }}>{config.label}</span>;
              }},
              { key: 'company_id', header: 'Scope', width: '120px', render: (val, row) => (
                <span style={{ padding: '2px 7px', borderRadius: '4px', fontSize: 'var(--font-size-xs)', fontWeight: 'var(--font-weight-semibold)', backgroundColor: !val ? `${colors.adminPurple}10` : `${colors.accent}10`, color: !val ? colors.adminPurple : colors.accent }}>
                  {row.company_name || 'Global'}
                </span>
              )},
              { key: 'source', header: 'Source', width: '110px', render: (val) => <span style={{ color: colors.textMuted }}>{val || 'Not available'}</span> },
              { key: 'document_date', header: 'Date', width: '90px', render: (val, row) => <span style={{ color: colors.textMuted }}>{formatDate(row.document_date || row.created_at)}</span> },
            ]}
            data={paginatedDocs}
            onRowClick={(row) => setSelectedDocument(selectedDocument?.document_id === row.document_id ? null : row)}
            emptyMessage={searchQuery ? 'No documents found. Try a different search term' : 'No documents found. Upload your first document to get started'}
          />

          {/* Pagination Footer */}
          {filteredDocs.length > PAGE_SIZE && (
            <div style={{
              padding: '8px 16px', borderTop: `1px solid ${colors.mediumGray}`,
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              fontSize: 'var(--font-size-sm)', color: colors.textMuted,
            }}>
              <span>
                Showing {((currentPage - 1) * PAGE_SIZE) + 1} to {Math.min(currentPage * PAGE_SIZE, filteredDocs.length)} of {filteredDocs.length}
              </span>
              <div style={{ display: 'flex', gap: '4px' }}>
                <PaginationBtn label="‹ Prev" disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)} />
                {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                  let page;
                  if (totalPages <= 7) { page = i + 1; }
                  else if (currentPage <= 4) { page = i + 1; }
                  else if (currentPage >= totalPages - 3) { page = totalPages - 6 + i; }
                  else { page = currentPage - 3 + i; }
                  return <PaginationBtn key={page} label={String(page)} active={page === currentPage} onClick={() => setCurrentPage(page)} />;
                })}
                <PaginationBtn label="Next ›" disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)} />
              </div>
            </div>
          )}
        </div>

        {/* ── Detail Panel ── */}
        {selectedDocument && (
          <div style={{
            backgroundColor: colors.white, borderRadius: '8px',
            boxShadow: 'var(--shadow-sm)', overflow: 'hidden',
            position: 'sticky', top: '24px', alignSelf: 'start',
          }}>
            <div style={{
              padding: '12px 16px', borderBottom: `1px solid ${colors.mediumGray}`,
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}>
              <h3 style={{ fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: colors.darkText, margin: 0 }}>Document Details</h3>
              <button onClick={() => setSelectedDocument(null)} style={{ background: 'none', border: 'none', fontSize: 'var(--font-size-lg)', color: colors.textMuted, cursor: 'pointer', padding: '2px' }}>×</button>
            </div>

            <div style={{ padding: '12px' }}>
              <h4 style={{ fontSize: 'var(--font-size-base)', fontWeight: 'var(--font-weight-semibold)', color: colors.darkText, margin: '0 0 14px 0', lineHeight: '1.4' }}>
                {selectedDocument.title}
              </h4>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <DetailRow label="Category">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: 'var(--font-size-base)' }}>{getCategoryConfig(selectedDocument.category).icon}</span>
                    <span>{selectedDocument.category_name || getCategoryConfig(selectedDocument.category).label}</span>
                  </div>
                </DetailRow>

                <DetailRow label="Scope">
                  <span style={{
                    padding: '2px 7px', borderRadius: '4px', fontSize: 'var(--font-size-xs)', fontWeight: 'var(--font-weight-semibold)',
                    backgroundColor: !selectedDocument.company_id ? `${colors.adminPurple}10` : `${colors.accent}10`,
                    color: !selectedDocument.company_id ? colors.adminPurple : colors.accent,
                  }}>
                    {selectedDocument.company_name || 'Global'}
                  </span>
                </DetailRow>

                {selectedDocument.source && <DetailRow label="Source">{selectedDocument.source}</DetailRow>}
                <DetailRow label="Date">{formatDate(selectedDocument.document_date)}</DetailRow>
                <DetailRow label="File">{selectedDocument.file_name}</DetailRow>

                {selectedDocument.has_content && (
                  <DetailRow label="Content">
                    <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: 'var(--font-size-xs)', fontWeight: 'var(--font-weight-semibold)', backgroundColor: `${colors.success}15`, color: colors.success }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: 'middle' }}><polyline points="20 6 9 17 4 12"/></svg> {selectedDocument.text_length?.toLocaleString()} chars
                    </span>
                  </DetailRow>
                )}

                {selectedDocument.summary && (
                  <DetailRow label="Summary">
                    <div style={{ fontSize: 'var(--font-size-sm)', lineHeight: '1.5', color: colors.darkText }}>{selectedDocument.summary}</div>
                  </DetailRow>
                )}

                {selectedDocument.relevance_tags?.length > 0 && (
                  <DetailRow label="Tags">
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                      {selectedDocument.relevance_tags.map((tag, i) => (
                        <span key={i} style={{ padding: '2px 8px', borderRadius: '10px', fontSize: 'var(--font-size-xs)', fontWeight: 'var(--font-weight-medium)', backgroundColor: colors.lightGray, color: colors.darkText }}>{tag}</span>
                      ))}
                    </div>
                  </DetailRow>
                )}

                <div style={{ paddingTop: '10px', borderTop: `1px solid ${colors.mediumGray}` }}>
                  <button
                    onClick={() => handleDelete(selectedDocument.document_id, selectedDocument.title)}
                    style={{
                      width: '100%', padding: '8px', backgroundColor: `${colors.danger}08`, color: colors.danger,
                      border: `1px solid ${colors.danger}25`, borderRadius: '6px', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-medium)', cursor: 'pointer',
                    }}
                  >Delete Document</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Upload Modal ── */}
      <Modal isOpen={showUploadModal} onClose={() => { setShowUploadModal(false); resetForm(); }} title="Upload Document" size="medium">

            <form onSubmit={handleUpload} style={{ padding: '18px 20px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                {/* Company Scope */}
                <ModalField label="Company Scope *">
                  <select
                    value={uploadForm.company_id}
                    onChange={(e) => setUploadForm({ ...uploadForm, company_id: e.target.value })}
                    style={inputStyle}
                  >
                    <option value="">Global (all companies)</option>
                    {companies.map(c => (
                      <option key={c.company_id} value={c.company_id}>{c.company_name}</option>
                    ))}
                  </select>
                  <div style={{ fontSize: 'var(--font-size-xs)', color: colors.textMuted, marginTop: '3px' }}>
                    {uploadForm.company_id
                      ? `Only users from ${companies.find(c => String(c.company_id) === String(uploadForm.company_id))?.company_name || 'this company'} will see this document`
                      : 'All companies on the platform will see this document'
                    }
                  </div>
                </ModalField>

                {/* File */}
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'var(--font-weight-medium)', fontSize: 'var(--font-size-sm)', color: colors.darkText }}>Document File *</label>
                  <input type="file" accept=".pdf,.docx,.doc,.txt,.md"
                    onChange={(e) => setUploadForm({ ...uploadForm, file: e.target.files[0] })}
                    style={{ width: '100%', padding: '12px 16px', border: `2px dashed ${colors.mediumGray}`, borderRadius: '6px', backgroundColor: colors.lightGray, boxSizing: 'border-box', fontSize: 'var(--font-size-sm)' }}
                  />
                  <div style={{ fontSize: 'var(--font-size-xs)', color: colors.textMuted, marginTop: '3px' }}>PDF, DOCX, DOC, TXT, MD</div>
                </div>

                <ModalField label="Title *">
                  <input type="text" value={uploadForm.title}
                    onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })}
                    placeholder="e.g., Q4 2025 Aerospace Metals Report" style={inputStyle} />
                </ModalField>

                <ModalField label="Category *">
                  <select value={uploadForm.category}
                    onChange={(e) => setUploadForm({ ...uploadForm, category: e.target.value })}
                    style={inputStyle}>
                    {Object.entries(GLOBAL_CATEGORIES).map(([key, config]) => (
                      <option key={key} value={key}>{config.label}</option>
                    ))}
                  </select>
                </ModalField>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <ModalField label="Source">
                    <input type="text" value={uploadForm.source}
                      onChange={(e) => setUploadForm({ ...uploadForm, source: e.target.value })}
                      placeholder="e.g., McKinsey" style={inputStyle} />
                  </ModalField>
                  <ModalField label="Document Date">
                    <input type="date" value={uploadForm.document_date}
                      onChange={(e) => setUploadForm({ ...uploadForm, document_date: e.target.value })}
                      style={inputStyle} />
                  </ModalField>
                </div>

                <ModalField label="Key Takeaway / Summary">
                  <textarea value={uploadForm.summary}
                    onChange={(e) => setUploadForm({ ...uploadForm, summary: e.target.value })}
                    placeholder="Main insight the AI should know" rows={2}
                    style={{ ...inputStyle, resize: 'vertical' }} />
                </ModalField>

                <ModalField label="Relevance Tags">
                  <input type="text" value={uploadForm.relevance_tags}
                    onChange={(e) => setUploadForm({ ...uploadForm, relevance_tags: e.target.value })}
                    placeholder="e.g., titanium, supply chain (comma-separated)" style={inputStyle} />
                </ModalField>

                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '4px' }}>
                  <button type="button" onClick={() => { setShowUploadModal(false); resetForm(); }}
                    style={{ padding: '8px 16px', backgroundColor: 'transparent', border: `1px solid ${colors.mediumGray}`, borderRadius: '6px', fontSize: 'var(--font-size-sm)', cursor: 'pointer', color: colors.darkText }}>
                    Cancel
                  </button>
                  <button type="submit" disabled={uploading}
                    style={{ padding: '8px 16px', backgroundColor: colors.adminPurple, color: colors.white, border: 'none', borderRadius: '6px', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', cursor: uploading ? 'not-allowed' : 'pointer', opacity: uploading ? 0.7 : 1 }}>
                    {uploading ? 'Uploading...' : 'Upload Document'}
                  </button>
                </div>
              </div>
            </form>
      </Modal>
    </div>
  );
}

// ── Shared Styles ──
const inputStyle = {
  width: '100%', padding: '8px 10px', fontSize: 'var(--font-size-sm)',
  border: `1px solid ${colors.mediumGray}`, borderRadius: '6px',
  outline: 'none', boxSizing: 'border-box', backgroundColor: colors.white,
};

// ── Helper Components ──
function DetailRow({ label, children }) {
  return (
    <div>
      <div style={{ fontSize: 'var(--font-size-xs)', color: colors.textMuted, textTransform: 'uppercase', letterSpacing: 'var(--letter-spacing-wide)', marginBottom: '2px', fontWeight: 'var(--font-weight-semibold)' }}>{label}</div>
      <div style={{ fontSize: 'var(--font-size-sm)', color: colors.darkText }}>{children}</div>
    </div>
  );
}

function ModalField({ label, children }) {
  return (
    <div>
      <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'var(--font-weight-medium)', fontSize: 'var(--font-size-sm)', color: colors.darkText }}>{label}</label>
      {children}
    </div>
  );
}

function PaginationBtn({ label, active, disabled, onClick }) {
  return (
    <button onClick={onClick} disabled={disabled}
      style={{
        padding: '4px 9px', fontSize: 'var(--font-size-xs)', fontWeight: active ? 'var(--font-weight-bold)' : 'var(--font-weight-medium)',
        border: `1px solid ${active ? colors.adminPurple : colors.mediumGray}`,
        borderRadius: '4px', cursor: disabled ? 'not-allowed' : 'pointer',
        backgroundColor: active ? colors.adminPurple : colors.white,
        color: active ? colors.white : disabled ? colors.mediumGray : colors.darkText,
        opacity: disabled ? 0.5 : 1,
      }}>
      {label}
    </button>
  );
}

export default AdminKnowledge;
